import {
  Button,
  collapseColumn,
  column,
  Css,
  dateColumn,
  dateRangeFilter,
  DateRangeFilterValue,
  defaultPage,
  emptyCell,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  HeaderAction,
  ModalBody,
  ModalFooter,
  ModalHeader,
  numericColumn,
  PageHeaderLayout,
  selectColumn,
  simpleHeader,
  singleFilter,
  TriggerNoticeProps,
  useComputed,
  useGridTableApi,
  useGridTableLayoutState,
  useModal,
  useSnackbar,
} from "@homebound/beam";
import { capitalCase } from "change-case";
import { useCallback, useMemo } from "react";
import { chipCell, dateCell, linkHeader, priceCell, tagCell } from "src/components";
import { baseDownloadUrl } from "src/context";
import { Commitment, CommitmentsFilter, CommitmentStatus, DateOperation } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import {
  DevelopmentPurchaseOrderQuery,
  PurchaseOrderPage_CommitmentChangeOrderFragment,
  PurchaseOrderPage_CommitmentFragment,
  PurchaseOrderPage_DevelopmentFiltersFragment,
  useDevelopmentPurchaseOrderFiltersQuery,
  useDevelopmentPurchaseOrderQuery,
  useSaveCommitmentsMutation,
} from "src/routes/developments/purchase-orders/PurchaseOrdersPage.generated";
import { createDevelopmentContractOverviewUrl } from "src/RouteUrls";
import { commitmentStatusToTagTypeMapper, foldEnum, formatList, groupBy, pluralize } from "src/utils";
import { hasCompleteDateRange } from "src/utils/temporal";
import { openDownload } from "src/utils/window";
import {
  ReassignOption,
  useReassignPurchaseOrdersWizard,
} from "../reassign-purchase-orders/ReassignPurchaseOrdersWizard";

type PurchaseOrdersPageProps = { isGlobal?: boolean; isTrades?: boolean };

type DevelopmentIdParam = { developmentId: string | undefined };

export function PurchaseOrdersPage({ isGlobal, isTrades }: PurchaseOrdersPageProps) {
  const { developmentId } = useRouteParams<DevelopmentIdParam>();
  const { data: filterOptions } = useDevelopmentPurchaseOrderFiltersQuery({
    variables: { developmentId: developmentId! },
    fetchPolicy: "cache-first",
    skip: isGlobal,
  });

  const filterDefs: FilterDefs<FilterOptions> = useMemo(
    () => createFilters(filterOptions?.development),
    [filterOptions?.development],
  );
  const layoutState = useGridTableLayoutState<FilterOptions>({
    persistedFilter: { filterDefs, storageKey: isGlobal ? "globalPurchaseOrdersFilter" : "purchaseOrdersFilter" },
    search: "server",
    groupBy: { project: "Project", tradePartner: "Trade partner" },
  });

  const query = useDevelopmentPurchaseOrderQuery({
    variables: {
      filter: { ...applyFilters(layoutState.filter), search: layoutState.searchString ?? "", developmentId },
      page: defaultPage,
    },
  });

  const body = (
    <PurchaseOrdersView developmentId={developmentId} query={query} layoutState={layoutState} showActions={!isGlobal} />
  );

  if (!isGlobal || isTrades) {
    return <PageHeaderLayout pageHeader={{ title: "Purchase Orders" }}>{body}</PageHeaderLayout>;
  }

  return body;
}

type PurchaseOrdersViewProps = DevelopmentIdParam & {
  layoutState: ReturnType<typeof useGridTableLayoutState<FilterOptions>>;
  query: ReturnType<typeof useDevelopmentPurchaseOrderQuery>;
  showActions: boolean;
};

function PurchaseOrdersView(props: PurchaseOrdersViewProps) {
  const { developmentId, query, layoutState, showActions } = props;
  const { openModal } = useModal();
  const { triggerNotice } = useSnackbar();
  const tableApi = useGridTableApi<Row>();
  const selectedCommitments = useComputed(() => {
    return tableApi.getSelectedRows().filter((r) => r.kind === "co" || r.kind === "commitment") as GridDataRow<
      CommitmentRow | CommitmentChangeOrderRow
    >[];
  }, [tableApi]);
  const [saveCommitmentsMutation] = useSaveCommitmentsMutation();
  const groupByValue = (layoutState.groupBy?.value ?? GroupByValue.project) as GroupByValue;

  const onVoid = useCallback(async () => {
    if (!validateSelectedCommitments(selectedCommitments, "voided", triggerNotice)) return;
    return openModal({
      content: (
        <ConfirmationModal
          onConfirmAction={async () => {
            const { data } = await saveCommitmentsMutation({
              variables: { input: selectedCommitments.map((c) => ({ id: c.id, status: CommitmentStatus.Voided })) },
              // after mutation completes, update cache to remove deleted draft commitments
              update: (cache) =>
                selectedCommitments
                  .filter((c) => c.data.status === CommitmentStatus.Draft)
                  .forEach((c) => cache.evict({ id: `Commitment:${c.id}` })),
            });
            if (data) {
              const savedCommitments = data.saveCommitments.commitments;
              const savedChangeOrders = savedCommitments.flatMap((c) => c.changeOrders);
              // 1 Purchase Order and 3 Change Orders have been voided successfully
              // if no mutation errors were thrown, we can assume all selectedCommitments were successfully voided
              triggerNotice({
                message: `${selectedCommitments.length} ${pluralize(savedCommitments, "Purchase Order")}
                ${
                  !savedChangeOrders.isEmpty
                    ? `and ${savedChangeOrders.length} ${pluralize(savedChangeOrders, "Change Order")}`
                    : ""
                } ${pluralize([...selectedCommitments, ...savedChangeOrders], "has", "have")}
                been voided successfully`,
                icon: "success",
              });
            }
          }}
          title="Void Purchase Orders"
          label="Void"
          confirmationMessage={
            <>
              <p css={Css.sm.mb2.$}>
                {selectedCommitments.length} {pluralize(selectedCommitments, "purchase order")} selected.
              </p>
              <p>
                Are you sure you want to void
                {pluralize(selectedCommitments, " this purchase order", " these purchase orders")}? This action cannot
                be undone.
              </p>
            </>
          }
        />
      ),
    });
  }, [selectedCommitments, saveCommitmentsMutation, openModal, triggerNotice]);

  const onChangeTradePartner = useCallback(() => {
    if (!validateSelectedCommitments(selectedCommitments, "edited", triggerNotice)) return;
    return openModal({
      content: (
        <ReassignPurchaseOrderModal
          mode="trade partner"
          selectedCommitmentIds={selectedCommitments.map((sc) => sc.id)}
        />
      ),
    });
  }, [selectedCommitments, openModal, triggerNotice]);

  const onChangeVersion = useCallback(() => {
    if (!validateSelectedCommitments(selectedCommitments, "edited", triggerNotice)) return;
    return openModal({
      content: (
        <ReassignPurchaseOrderModal mode="version" selectedCommitmentIds={selectedCommitments.map((sc) => sc.id)} />
      ),
    });
  }, [selectedCommitments, openModal, triggerNotice]);

  const onDownload = useCallback(() => {
    openDownload(
      `${baseDownloadUrl()}/zip?type=commitments&ids=${selectedCommitments
        .map((cLike) => cLike.id)
        .sort()
        .join()}`,
    );
  }, [selectedCommitments]);

  const tableActions: HeaderAction[] = useMemo(
    () => [
      {
        kind: "menu",
        trigger: { icon: "verticalDots", variant: "outline" },
        tooltip: "Actions",
        items: [
          { label: "Void", disabled: !selectedCommitments.length, onClick: onVoid },
          { label: "Change Trade Partner", disabled: !selectedCommitments.length, onClick: onChangeTradePartner },
          { label: "Change Version", disabled: !selectedCommitments.length, onClick: onChangeVersion },
          { label: "Download PDFs", disabled: !selectedCommitments.length, onClick: onDownload },
        ],
      },
    ],
    [selectedCommitments, onVoid, onChangeTradePartner, onChangeVersion, onDownload],
  );

  const columns = useMemo(
    () => createColumns(developmentId, groupByValue, showActions),
    [developmentId, groupByValue, showActions],
  );
  const createTableRows = useCallback(
    (data: DevelopmentPurchaseOrderQuery | undefined) =>
      createRows(data?.commitmentsPage.commitments ?? [], groupByValue),
    [groupByValue],
  );
  const maybeFetchNextPage = useCallback(async () => {
    if (query.data?.commitmentsPage.pageInfo.hasNextPage) {
      await query.fetchMore({
        variables: { page: { offset: query.data.commitmentsPage.commitments.length, limit: defaultPage.limit } },
      });
    }
  }, [query]);

  return (
    <GridTableLayout
      layoutState={layoutState}
      hideEditColumns
      actions={showActions ? tableActions : undefined}
      tableProps={{
        api: tableApi,
        query,
        createRows: createTableRows,
        columns,
        as: "virtual",
        style: { grouped: true, rowHeight: "fixed" },
        emptyFallback: "No Commitments or Change Orders",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

type CommitmentChangeOrderRowData = PurchaseOrderPage_CommitmentChangeOrderFragment;
type CommitmentRowData = PurchaseOrderPage_CommitmentFragment;
type HeaderRow = { kind: "header" };
type GroupedRow = { kind: "group"; data: CommitmentRowData[] };
type CommitmentChangeOrderRow = { kind: "co"; data: CommitmentChangeOrderRowData };
type CommitmentRow = { kind: "commitment"; data: CommitmentRowData; children: CommitmentChangeOrderRow[] };
type Row = HeaderRow | GroupedRow | CommitmentRow | CommitmentChangeOrderRow;

const createColumns = (
  developmentId: DevelopmentIdParam["developmentId"],
  groupByKey: GroupByValue,
  showActions: boolean,
): GridColumn<Row>[] => [
  collapseColumn<Row>(),
  ...(showActions ? [selectColumn<Row>()] : []),
  column<Row>({
    header: "PO #",
    co: ({ accountingNumber, blueprintUrl }) => linkHeader(String(accountingNumber), blueprintUrl.path),
    commitment: ({ accountingNumber, blueprintUrl }) => linkHeader(String(accountingNumber), blueprintUrl.path),
    group: ([row]) => ({
      content: foldEnum(groupByKey, { tradePartner: row.tradePartner?.name, project: row.project.name }),
      colspan: 5,
    }),
    mw: "140px",
  }),
  column<Row>({
    header: "PO Type",
    group: emptyCell,
    co: () => "CO",
    commitment: (row) => (row.isBidCommitment ? "Dev PO" : "Ind PO"),
    w: "80px",
    mw: "80px",
  }),
  column<Row>({
    header: "Address",
    group: emptyCell,
    co: (row) => row.commitment.project.buildAddress?.street1,
    commitment: (row) => row.project.buildAddress?.street1,
    mw: "160px",
  }),
  column<Row>({
    header: "Trade Partner",
    co: ({ tradePartner }) => tradePartner?.name,
    commitment: ({ tradePartner }) => tradePartner?.name,
    group: emptyCell,
    mw: "160px",
  }),
  column<Row>({
    header: "Item Codes",
    co: ({ items }) => chipCell(items.map((i) => ({ text: i.fullCode, title: i.name }))),
    commitment: ({ items }) => chipCell(items.map((i) => ({ text: i.fullCode, title: i.name }))),
    group: emptyCell,
    w: 2,
    mw: "180px",
  }),
  dateColumn<Row>({
    header: "Released On",
    group: emptyCell,
    co: ({ executionDate }) => dateCell(executionDate),
    commitment: ({ executionDate }) => dateCell(executionDate),
    w: "110px",
    mw: "110px",
  }),
  numericColumn<Row>({
    header: "Version",
    co: (row) => {
      const bidContractRevision = row.commitment.bidContractRevision;
      return bidContractRevision?.id && developmentId
        ? linkHeader(
            bidContractRevision.version,
            createDevelopmentContractOverviewUrl(developmentId, bidContractRevision?.id),
          )
        : undefined;
    },
    commitment: (row) => {
      const bidContractRevision = row.bidContractRevision;
      return bidContractRevision?.id && developmentId
        ? linkHeader(
            bidContractRevision.version,
            createDevelopmentContractOverviewUrl(developmentId, bidContractRevision?.id),
          )
        : undefined;
    },
    group: emptyCell,
    align: "right",
    w: "80px",
    mw: "80px",
  }),
  numericColumn<Row>({
    header: "Committed Cost",
    co: ({ committedInCents }) => priceCell({ valueInCents: committedInCents }),
    commitment: ({ committedInCents }) => priceCell({ valueInCents: committedInCents }),
    group: emptyCell,
    mw: "120px",
  }),
  column<Row>({
    header: "Status",
    co: (row) => tagCell(commitmentStatusToTagTypeMapper[row.status], row.statusText),
    commitment: (row) => tagCell(commitmentStatusToTagTypeMapper[row.status], row.statusText),
    group: emptyCell,
    clientSideSort: false,
    w: "110px",
    mw: "110px",
  }),
];

function createRows(commitments: PurchaseOrderPage_CommitmentFragment[], groupByKey: GroupByValue): GridDataRow<Row>[] {
  const groupedCommitments = groupPurchaseOrdersBy(commitments, groupByKey);
  return [
    simpleHeader,
    ...groupedCommitments.map(([id, commitments]) => ({
      kind: "group" as const,
      id: id,
      data: commitments,
      children: commitments.map((cl) => ({
        kind: "commitment" as const,
        id: cl.id,
        data: cl,
        inferSelectedState: false as const,
        children: cl.changeOrders.map((co) => ({ kind: "co" as const, id: co.id, data: co, initCollapsed: true })),
      })),
    })),
  ];
}

function groupPurchaseOrdersBy(commitments: PurchaseOrderPage_CommitmentFragment[], groupByKey: GroupByValue) {
  const groupedRows = groupBy(commitments, (cl) => {
    return foldEnum(groupByKey, { tradePartner: cl.tradePartner?.id || "", project: cl.project.id });
  });

  return Object.entries(groupedRows);
}

enum GroupByValue {
  project = "project",
  tradePartner = "tradePartner",
}

const validateSelectedCommitments = (
  commitmentsLike: GridDataRow<CommitmentRow | CommitmentChangeOrderRow>[],
  action: string,
  triggerNotice: (props: TriggerNoticeProps) => { close: () => void },
): boolean => {
  const validationAlert = (accountingNumbers: number[], reason: string) =>
    triggerNotice({
      message: `${pluralize(commitmentsLike, "PO")}: ${formatList(
        accountingNumbers,
      )} can't be ${action} because ${reason}`,
      icon: "warning",
    });

  // Verify if selected rows are already voided
  const voidedCommitments = commitmentsLike
    .filter((sc) => sc.data.status === CommitmentStatus.Voided)
    .map((c) => c.data.accountingNumber);

  if (!voidedCommitments.isEmpty) {
    const reason = pluralize(voidedCommitments, "it's already been voided", "they have already been voided");
    validationAlert(voidedCommitments, reason);
    return false;
  }

  // Verify if all selected rows are commitments (not change orders) and are allowed to be voided
  const notVoidableCommitments = commitmentsLike
    .filter((c) => c.data.__typename === "CommitmentChangeOrder" || !(c.data as Commitment).canBeVoided.allowed)
    .map((c) => c.data.accountingNumber);

  if (!notVoidableCommitments.isEmpty) {
    const reason = pluralize(
      notVoidableCommitments,
      "it has a bill or is a commitment change order",
      "they have a bill or are commitment change orders",
    );
    validationAlert(notVoidableCommitments, reason);
    return false;
  }
  // Purchase orders are valid to be voided/reassigned
  return true;
};

type FilterOptions = Partial<{
  tradePartner: string;
  status: CommitmentStatus;
  costCode: string;
  executionDate: DateRangeFilterValue<string>;
}>;

function createFilters(developmentFilters?: PurchaseOrderPage_DevelopmentFiltersFragment) {
  return {
    status: singleFilter({
      options: Object.values(CommitmentStatus).map((cs) => ({ label: cs, value: cs })),
      getOptionLabel: (cs) => capitalCase(cs.label),
      getOptionValue: (cs) => cs.value,
      label: "Status",
    }),
    ...(developmentFilters
      ? {
          tradePartner: singleFilter({
            options: developmentFilters.tradePartners.sortByKey("name"),
            getOptionLabel: (tp) => tp.name,
            getOptionValue: (tp) => tp.id,
            label: "Trade Partner",
          }),
          costCode: singleFilter({
            options: developmentFilters.costCodes,
            getOptionLabel: (cc) => cc.number,
            getOptionValue: (cc) => cc.id,
            label: "Cost codes",
          }),
        }
      : {}),
    executionDate: dateRangeFilter({
      label: "Released on",
      defaultValue: { op: DateOperation.Between, value: undefined },
    }),
  };
}

function applyFilters(filter: FilterOptions): CommitmentsFilter {
  return {
    costCodes: filter.costCode ? [filter.costCode] : undefined,
    tradePartners: filter.tradePartner ? [filter.tradePartner] : undefined,
    status: filter.status ? [filter.status] : undefined,
    executionDate: hasCompleteDateRange(filter.executionDate?.value)
      ? { op: DateOperation.Between, value: filter.executionDate.value.from!, value2: filter.executionDate.value.to! }
      : undefined,
  };
}

type ReassignPurchaseOrderModalProps = { mode: ReassignOption; selectedCommitmentIds: string[] };

function ReassignPurchaseOrderModal(props: ReassignPurchaseOrderModalProps) {
  const { mode, selectedCommitmentIds } = props;
  const { closeModal } = useModal();
  const openWizard = useReassignPurchaseOrdersWizard(mode, selectedCommitmentIds);

  return (
    <>
      <ModalHeader>Change {capitalCase(mode)}</ModalHeader>
      <ModalBody>
        <div>
          <p css={Css.sm.mb2.$}>Are you sure you want to change the {mode} for the selected purchase orders (POs)?</p>
          <p>
            The proposed {mode} MUST be able to cover all cost codes in the selected POs. To change the trade for part
            of a PO, void the original PO and choose the new trade in the Lot Release flow.
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button
          label={`Change ${capitalCase(mode)}`}
          onClick={() => {
            closeModal();
            openWizard();
          }}
        />
      </ModalFooter>
    </>
  );
}
